import { useEffect, useState } from "react";
import { CalendarClock, Plus, Trash2, AlertTriangle, Sparkles, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/PageHero";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { TIPI_SCADENZA, STATI_SCADENZA } from "@/lib/constants";
import { toast } from "sonner";

const statoBadge = (d) => {
  if (d.stato === "chiusa") return <Badge className="bg-slate-100 text-slate-500 border-slate-200">Chiusa</Badge>;
  if (d.scaduta) return <Badge className="bg-red-100 text-red-700 border-red-200">Scaduta</Badge>;
  if (d.in_avvicinamento) return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Tra {d.giorni_rimanenti} gg</Badge>;
  return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Pianificata</Badge>;
};

export default function Deadlines() {
  const { tenantId, organizations, currentOrg } = useTenant();
  const { user } = useAuth();
  const isConsultant = user?.role === "consultant";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ tipo: "audit interno", descrizione: "", data_scadenza: "", stato: "aperta", tenant_id: "" });
  const [month, setMonth] = useState(new Date());
  const [genLoading, setGenLoading] = useState(false);

  const genReviews = async () => {
    if (!tenantId) { toast.error("Seleziona un cliente"); return; }
    setGenLoading(true);
    try {
      const r = await api.post(`/clients/${tenantId}/generate-review-deadlines`);
      if (r.data.created > 0) toast.success(`${r.data.created} scadenze di revisione create per le procedure`);
      else toast.info("Nessuna nuova scadenza: le procedure hanno già una revisione pianificata");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setGenLoading(false); }
  };

  const load = () => {
    const p = tenantId ? `?tenant_id=${tenantId}` : "";
    api.get(`/deadlines${p}`).then((r) => setItems(r.data));
  };
  useEffect(load, [tenantId]);

  const openNew = () => {
    setForm({ tipo: "audit interno", descrizione: "", data_scadenza: "", stato: "aperta", tenant_id: tenantId || "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.tenant_id) { toast.error("Seleziona un cliente"); return; }
    if (!form.data_scadenza) { toast.error("Inserisci la data"); return; }
    try { await api.post("/deadlines", form); toast.success("Scadenza creata"); setOpen(false); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => { await api.delete(`/deadlines/${id}`); load(); };
  const setStato = async (d, stato) => { await api.put(`/deadlines/${d.id}`, { ...d, stato }); load(); };

  const deadlineDates = items.filter((d) => d.stato !== "chiusa").map((d) => { try { return new Date(d.data_scadenza + "T00:00:00"); } catch { return null; } }).filter(Boolean);

  return (
    <div className="space-y-6">
      <PageHero label="Pianificazione" title="Agenda scadenze" subtitle={tenantId ? currentOrg?.nome : "Tutti i clienti"}>
        {isConsultant && tenantId && (
          <Button onClick={genReviews} disabled={genLoading} variant="outline" data-testid="gen-reviews-btn"
            className="mr-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50">
            {genLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            Genera revisioni procedure
          </Button>
        )}
        {isConsultant && (
          <Button onClick={openNew} data-testid="new-deadline-btn" className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">
            <Plus className="h-4 w-4 mr-2" /> Nuova scadenza
          </Button>
        )}
      </PageHero>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card className="border-slate-200">
            {items.length === 0 ? (
              <div className="p-12 text-center">
                <CalendarClock className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Nessuna scadenza registrata.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100" data-testid="deadlines-list">
                {items.map((d) => (
                  <div key={d.id} data-testid={`deadline-${d.id}`} className={`flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-4 ${d.scaduta ? "bg-red-50/40" : ""}`}>
                    <div className="flex items-start gap-3 min-w-0">
                      {d.scaduta && <AlertTriangle className="h-4 w-4 text-red-500 mt-1 shrink-0" />}
                      <div className="min-w-0">
                        <div className="text-sm font-medium capitalize">{d.tipo}</div>
                        <div className="text-xs text-slate-500">{d.descrizione}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{d.data_scadenza}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {statoBadge(d)}
                      {d.readonly ? (
                        <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 capitalize">{d.tipo}</Badge>
                      ) : (
                        <Select value={d.stato} onValueChange={(v) => setStato(d, v)} disabled={!isConsultant}>
                          <SelectTrigger className="h-8 w-36 text-xs" data-testid={`deadline-stato-${d.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{STATI_SCADENZA.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                      {isConsultant && !d.readonly && <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => remove(d.id)}><Trash2 className="h-4 w-4" /></Button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        <Card className="p-3 sm:p-4 border-slate-200 h-fit overflow-x-auto">
          <h3 className="text-sm font-medium mb-2 px-2">Calendario</h3>
          <Calendar
            mode="single"
            month={month}
            onMonthChange={setMonth}
            modifiers={{ deadline: deadlineDates }}
            modifiersClassNames={{ deadline: "bg-blue-600 text-white rounded-md font-semibold" }}
            className="rounded-md mx-auto"
          />
          <p className="text-xs text-slate-500 mt-2 px-2">I giorni evidenziati hanno scadenze aperte.</p>
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuova scadenza</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Cliente</Label>
              <Select value={form.tenant_id} onValueChange={(v) => setForm({ ...form, tenant_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="deadline-tenant"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                <SelectContent>{organizations.map((o) => <SelectItem key={o.id} value={o.id}>{o.nome}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger className="mt-1.5" data-testid="deadline-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>{TIPI_SCADENZA.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Descrizione</Label><Input value={form.descrizione} data-testid="deadline-descrizione" onChange={(e) => setForm({ ...form, descrizione: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Data scadenza</Label><Input type="date" value={form.data_scadenza} data-testid="deadline-data" onChange={(e) => setForm({ ...form, data_scadenza: e.target.value })} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="save-deadline-btn" className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
